/**
 * What a member of a merged document will OUTPUT, what is VISIBLE on its
 * canvas, and what governed a burn.
 *
 * The two answers differ only while the Bates panel is open: output ignores
 * the preview, visible follows it. Everything that leaves the editor reads
 * output; only the page reads visible. Collapsing them is how the rail badge
 * came to report a preview as applied (VE-31894).
 *
 * Members store nothing: a document in a merged set carries no Bates settings
 * of its own, so its stamp is resolved on every render from the project plus
 * its own position — and from which members before it have been worked on,
 * since one the merge leaves out spans no numbers (VE-32395). That is what
 * makes the numbering appear when the reviewer switches to the next document
 * without anything having been written to it.
 *
 * Composing three slices — the sessions (unsaved edits) and the panel's
 * preview from documents,
 * the record from projects, the request's files from casemedia, whose
 * `details.veritoneFile.pageCount` is each member's ORIGINAL page count and so
 * its span — is why this is here rather than in `documents/selectors`. An
 * earlier version of this header claimed that module was unreachable from the
 * burn-in worker's import graph; it is not in that graph either way (the
 * worker imports seven leaf modules and nothing reaches back), so the claim
 * was false and is removed rather than restated.
 *
 * The REAL reason is narrower: `documents/selectors` has no business importing
 * the folder view's state, and this needs it for page counts.
 */
import { createSelector } from '@reduxjs/toolkit';

import {
  selectEditorRequestDocuments,
  selectEditorRequestFolder,
} from '@redact-modules/casemedia/selectors';
import { readFilePageCount } from '@redact-modules/casemedia/pageCount';
import { neverWorkedOnIds } from '@redact-modules/casemedia/redactAllWorkList';
import {
  selectBatesPreview,
  selectBatesStamp,
  selectCurrentTdoId,
  selectUnsavedDocuments,
} from '@redact-modules/documents/selectors';
import type { BatesStampSettings } from '@redact-modules/documents/models';
import type { TDOId } from '@common-modules/universal/models/Brands';

import { resolveGoverningBates, type GoverningBates } from './batesStamp';
import {
  projectOfFolder,
  selectProjectBatesStamp,
  selectProjectMembers,
} from './selectors';

type BatesRenderState = Parameters<typeof selectBatesStamp>[0] &
  Parameters<typeof selectEditorRequestDocuments>[0] &
  Parameters<ReturnType<typeof selectProjectBatesStamp>>[0];

/**
 * The project whose Bates stamping governs the open request, or undefined.
 *
 * SCOPED TO THE OPEN REQUEST, not to the `tdoId` asked about: page counts and
 * membership both come from the request's files, and there is no index of
 * TDO to project to resolve one in isolation. A caller that outlives the
 * editor — a burn-in — must therefore read this AT THE CLICK and carry the
 * answer, which is what `runDocumentBurnIn` does; read later it comes back
 * undefined, because returning to the folder view clears the request.
 */
const selectGoverningProjectId = (state: BatesRenderState) =>
  projectOfFolder(selectEditorRequestFolder(state))?.tdoId;

/**
 * Page counts for the request's files, keyed by TDO.
 *
 * Its own selector so the Map is rebuilt only when the file list changes, not
 * on every read — `DocumentPage` mounts one component per page and reads this
 * through the resolver below.
 */
const selectRequestPageCounts = createSelector(
  [selectEditorRequestDocuments],
  (documents): ReadonlyMap<string, number | null> =>
    new Map(
      documents.map((file) => [
        String(file.id),
        readFilePageCount(file.details),
      ])
    )
);

/**
 * The request's files the merge would LEAVE OUT, which span no Bates numbers
 * (VE-32395; see `effectiveBatesStarts`).
 *
 * Over every file of the request rather than the project's members: the
 * predicate only ever looks up the ids it is given, and `members` is per
 * project, so filtering here would cost a selector per project for nothing.
 * The rows answer for saves (the editor patches them, `redactionDataSaved`);
 * edits not saved yet are asked of the sessions.
 */
const selectRequestNeverWorkedOnIds = createSelector(
  [selectEditorRequestDocuments, selectUnsavedDocuments],
  (documents, unsavedDocuments): ReadonlySet<string> =>
    neverWorkedOnIds({
      files: documents,
      memberIds: documents.map((file) => String(file.id)),
      unsavedTdoIds: new Set(unsavedDocuments.map((doc) => String(doc.tdoId))),
    })
);

/**
 * Whether a merged document's stamping governs one named file.
 *
 * THREE ANSWERS, because two could not tell "nothing governs this file" from
 * "we cannot say yet". Both used to arrive as `null`, and the burn read that
 * as the first — so while the record or the membership was still loading, or
 * permanently after a failed `LOAD_COMBINED`, a member was burned with its own
 * leftover per-file stamp and two documents in one release came out numbered
 * from the same place, each reported current.
 *
 * `unresolved` is the answer that must not be guessed past: draw nothing, burn
 * nothing, and record no provenance.
 */
export type BatesGovernance =
  | { readonly status: 'governed'; readonly bates: GoverningBates }
  | { readonly status: 'unresolved' }
  | { readonly status: 'ungoverned' };

const UNRESOLVED: BatesGovernance = { status: 'unresolved' };
const UNGOVERNED: BatesGovernance = { status: 'ungoverned' };

/**
 * Governed answers, per record and file, so an unchanged one keeps its
 * reference (VE-31894). The selectors below feed `DocumentEditorView` and
 * every `DocumentPage` through `useSelector`; a fresh object per call defeated
 * their memos and re-rendered those views on every action while a member was
 * open.
 */
const governanceCache = new WeakMap<object, Map<string, BatesGovernance>>();

/**
 * What governs ONE named file — the shape the burn-in needs.
 *
 * TAKES A `tdoId` rather than reading the open session, because a burn-in is a
 * long-lived fork that survives a document switch: resolving against whatever
 * is on screen when the fetch returns would stamp one file with another's
 * numbering.
 *
 * `excludedIds` is the members a press's merge will leave out, when the press
 * has decided that itself. A reviewer can include files nobody has worked on
 * (VE-32440); those are burned in the same pass and must span their numbers,
 * though the state still calls them left out. Omitted, the state's answer
 * is used (`selectRequestNeverWorkedOnIds`).
 */
export const selectBatesGovernanceFor =
  (tdoId: TDOId | string | undefined, excludedIds?: ReadonlySet<string>) =>
  (state: BatesRenderState): BatesGovernance => {
    const projectTdoId = selectGoverningProjectId(state);
    if (!projectTdoId || !tdoId) {
      return UNGOVERNED;
    }
    const record = selectProjectBatesStamp(projectTdoId)(state);
    const members = selectProjectMembers(projectTdoId)(state);
    // Two independent loads, and either being outstanding is `unresolved`.
    // `record === undefined` is the one that bites: only LOAD_COMBINED_SUCCESS
    // writes that key, so a failed lookup leaves it unread for the session.
    if (record === undefined || members === undefined) {
      return UNRESOLVED;
    }
    // The set carries no stamping, or this file is not in it. Either way the
    // file's own per-file stamp is its own business.
    if (
      record === null ||
      !members.some((id) => String(id) === String(tdoId))
    ) {
      return UNGOVERNED;
    }
    const pageCounts = selectRequestPageCounts(state);
    const bates = resolveGoverningBates({
      record,
      members,
      pageCountOf: (id) => pageCounts.get(String(id)) ?? null,
      tdoId,
      excludedIds: excludedIds ?? selectRequestNeverWorkedOnIds(state),
    });
    // A member whose start is not computable yet: a real member, no answer.
    if (!bates) {
      return UNRESOLVED;
    }
    // Keyed on the RECORD object, so settings and identity are fixed per
    // entry; the start and span are all a reorder or page count can move.
    let byTdo = governanceCache.get(record);
    if (!byTdo) {
      byTdo = new Map();
      governanceCache.set(record, byTdo);
    }
    const key = `${String(projectTdoId)}|${String(tdoId)}`;
    const cached = byTdo.get(key);
    if (
      cached?.status === 'governed' &&
      cached.bates.startNumber === bates.startNumber &&
      cached.bates.assumedPageCount === bates.assumedPageCount
    ) {
      return cached;
    }
    const next: BatesGovernance = { status: 'governed', bates };
    byTdo.set(key, next);
    return next;
  };

/** Governance of the document on screen. */
const selectCurrentBatesGovernance = (state: BatesRenderState) =>
  selectBatesGovernanceFor(selectCurrentTdoId(state) ?? undefined)(state);

/**
 * THE STAMP THE REDACTED FILE WILL CARRY — never the panel's preview
 * (VE-31894).
 *
 *  1. the project's settings at this member's start;
 *  2. the document's own stamp, for a file no merged document governs.
 *
 * A MEMBER WHOSE START IS NOT YET KNOWN OUTPUTS NOTHING — not its own stamp. A
 * member can still be carrying a per-file stamp from before it joined, and
 * falling back to it was how two documents in one release could end up
 * numbered from the same place, with every check reporting them current.
 *
 * Unlike its watermark counterpart there is no hide switch to fold in: a Bates
 * stamp is absent or it is not, so output and "applied" coincide here.
 *
 * Drives the rail's badge and the output verdict. Reading the VISIBLE stamp
 * instead lit the badge the moment the panel opened, before Apply, and nulled
 * the verdict's expected start while the panel's switch was off.
 */
export const selectOutputMergedBatesStamp = createSelector(
  [selectBatesStamp, selectCurrentBatesGovernance],
  (committed, governance): BatesStampSettings | null => {
    if (governance.status !== 'governed') {
      return governance.status === 'unresolved' ? null : committed;
    }
    return governance.bates.settings;
  }
);

/**
 * WHAT THE CANVAS SHOWS: the output stamp above, unless the panel's live
 * preview is open.
 *
 * The preview wins outright rather than merging — including when it is turned
 * off, which is how the switch hides an applied stamp without removing it —
 * but takes this member's start, or every document previews as the set's first
 * and jumps the moment the panel closes. A member whose start is unknown draws
 * nothing, preview or not. Everything that produces output reads the output
 * stamp, so an unapplied preview can never leave the screen.
 */
export const selectVisibleMergedBatesStamp = createSelector(
  [
    selectOutputMergedBatesStamp,
    selectBatesPreview,
    selectCurrentBatesGovernance,
  ],
  (applied, preview, governance): BatesStampSettings | null => {
    if (!preview || governance.status === 'unresolved') {
      return applied;
    }
    if (!preview.enabled) {
      return null;
    }
    return governance.status === 'governed'
      ? { ...preview.settings, startNumber: governance.bates.startNumber }
      : preview.settings;
  }
);
